import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

function ServicePerformanceTable({ services }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Service',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={600}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'booking_count',
        header: 'Bookings',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={500}>
            {getValue() || 0}
          </Typography>
        ),
      },
      {
        accessorKey: 'total_revenue',
        header: 'Total Revenue',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={600} color="success.main">
            £{(getValue() || 0).toFixed(2)}
          </Typography>
        ),
      },
      {
        accessorKey: 'average_price',
        header: 'Average Price',
        cell: ({ getValue }) => (
          <Typography variant="body2">
            £{(getValue() || 0).toFixed(2)}
          </Typography>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: services,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (services.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'info.light',
          borderRadius: 2,
          color: 'info.dark',
        }}
      >
        <Typography variant="body1">No data available</Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {services.map((service) => (
          <Paper
            key={service.id}
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              {service.name}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Bookings
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {service.booking_count || 0}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Revenue
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  £{(service.total_revenue || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Average Price
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                £{(service.average_price || 0).toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop table view with sorting
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell
                  key={header.id}
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'grey.50',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() && (
                      header.column.getIsSorted() === 'asc' ? (
                        <ArrowUpwardIcon fontSize="small" />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" />
                      )
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} hover>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ServicePerformanceTable;
